import React, { useState, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { useCollegePrepContext } from '../../../contexts/CollegePrepContext';
import ConversationalEssayBuilder from '../../College/ConversationalEssayBuilder';
import EssayRefinementInterface from '../../College/EssayRefinementInterface';
import EssayListView from './EssayListView';
import NewEssayForm from './NewEssayForm';
import TemplatePicker from './TemplatePicker';
import GuidedResponse from './GuidedResponse';
import { SUMMER_PROGRAM_TEMPLATE_REGISTRY, suggestTemplate } from './templates';

const EssayBuilder = () => {
  const { updateEssayDraft, programs, essayDrafts } = useCollegePrepContext();

  // View state: 'list' | 'new' | 'builder' | 'guided-response' | 'refinement'
  const [view, setView] = useState('list');
  const [activeDraft, setActiveDraft] = useState(null);

  // Determine which mode a draft should use
  const getModeForDraft = (draft) => {
    // If already has builder data, respect the mode it was created with
    if (draft.builderData?.mode === 'guided-response') return 'guided-response';
    if (draft.builderData?.template) return 'builder';
    // If it has a templateId set, use builder mode
    if (draft.templateId) return 'builder';
    // Default: use mode stored on the draft
    return draft.mode || 'guided-response';
  };

  // Handle selecting an essay from the list
  const handleSelectEssay = (draft) => {
    setActiveDraft(enrichDraft(draft));
    const mode = getModeForDraft(draft);

    if (draft.status === 'complete' && draft.content) {
      // For guided-response completed essays, go to refine (inline)
      // For builder completed essays, go to refinement interface
      if (mode === 'guided-response') {
        setView('guided-response');
      } else {
        setView('refinement');
      }
    } else {
      setView(mode === 'guided-response' ? 'guided-response' : 'builder');
    }
  };

  // Enrich draft with program name for display
  const enrichDraft = (draft) => {
    if (draft.programId) {
      const program = programs.find(p => p.id === draft.programId);
      return { ...draft, programName: program?.name || null };
    }
    return draft;
  };

  // Handle new essay created from form
  const handleEssayCreated = (draft) => {
    setActiveDraft(enrichDraft(draft));
    setView(draft.mode === 'guided-response' ? 'guided-response' : 'builder');
  };

  // Handle save from ConversationalEssayBuilder
  const handleSaveEssay = useCallback((saveData) => {
    if (!activeDraft) return;

    updateEssayDraft(activeDraft.id, {
      builderData: {
        template: saveData.template,
        sections: saveData.sections,
        currentStep: saveData.currentStep,
        wordCount: saveData.wordCount
      },
      content: saveData.content || '',
      templateId: saveData.template,
      status: 'in_progress'
    });
  }, [activeDraft, updateEssayDraft]);

  // Handle save from GuidedResponse
  const handleGuidedSave = useCallback((saveData) => {
    if (!activeDraft) return;

    updateEssayDraft(activeDraft.id, {
      builderData: saveData.builderData,
      content: saveData.content || '',
      status: saveData.status || 'in_progress'
    });

    // Keep local state in sync
    setActiveDraft(prev => ({
      ...prev,
      builderData: saveData.builderData,
      content: saveData.content || '',
      status: saveData.status || prev.status
    }));
  }, [activeDraft, updateEssayDraft]);

  // Handle weave (transition to refinement)
  const handleWeave = useCallback((essayData) => {
    if (!activeDraft) return;

    updateEssayDraft(activeDraft.id, {
      builderData: essayData.builder_data,
      content: essayData.content,
      status: 'complete'
    });

    setActiveDraft(prev => ({
      ...prev,
      content: essayData.content,
      builderData: essayData.builder_data,
      status: 'complete'
    }));

    toast.success('Time to refine your essay!');
    setView('refinement');
  }, [activeDraft, updateEssayDraft]);

  // Handle back from builder
  const handleBack = () => {
    setActiveDraft(null);
    setView('list');
  };

  // Refinement persistence callbacks (localStorage via context)
  const handleLoadAnalysis = useCallback(async () => {
    if (!activeDraft?.refinementAnalysis) return null;
    return {
      analysis: activeDraft.refinementAnalysis.feedback,
      version: activeDraft.refinementAnalysis.analysisVersion,
      lastAnalyzedAt: activeDraft.refinementAnalysis.lastAnalyzedAt,
      wordCount: activeDraft.refinementAnalysis.analysisWordCount
    };
  }, [activeDraft]);

  const handleSaveAnalysis = useCallback(async (essayId, analysisData) => {
    if (!activeDraft) return;
    updateEssayDraft(activeDraft.id, {
      refinementAnalysis: analysisData
    });
    setActiveDraft(prev => ({
      ...prev,
      refinementAnalysis: analysisData
    }));
  }, [activeDraft, updateEssayDraft]);

  const handleSaveDraft = useCallback(async (essayId, draftContent, wordCount) => {
    if (!activeDraft) return;
    updateEssayDraft(activeDraft.id, {
      content: draftContent,
      builderData: activeDraft.builderData
        ? { ...activeDraft.builderData, wordCount }
        : { wordCount }
    });
    setActiveDraft(prev => ({
      ...prev,
      content: draftContent
    }));
  }, [activeDraft, updateEssayDraft]);

  // Render template selection for the builder
  const renderTemplateSelection = useCallback((onSelectTemplate) => {
    const suggestedId = activeDraft?.promptText ? suggestTemplate(activeDraft.promptText) : null;
    return (
      <TemplatePicker
        onSelectTemplate={onSelectTemplate}
        suggestedTemplateId={activeDraft?.templateId || suggestedId}
      />
    );
  }, [activeDraft]);

  // Get word goal from template or draft's word limit
  const getWordGoal = () => {
    if (activeDraft?.wordLimit) return activeDraft.wordLimit;
    if (activeDraft?.templateId && SUMMER_PROGRAM_TEMPLATE_REGISTRY[activeDraft.templateId]) {
      return SUMMER_PROGRAM_TEMPLATE_REGISTRY[activeDraft.templateId].wordTarget;
    }
    return 300;
  };

  // Build selectedEssay object for the ConversationalEssayBuilder
  const getSelectedEssay = () => {
    if (!activeDraft) return null;
    return {
      id: activeDraft.id,
      title: activeDraft.promptText?.slice(0, 60) || 'Essay Draft',
      content: activeDraft.content || '',
      builder_data: activeDraft.builderData || null
    };
  };

  // === VIEWS ===

  if (view === 'new') {
    return (
      <NewEssayForm
        onCreated={handleEssayCreated}
        onCancel={() => setView('list')}
      />
    );
  }

  if (view === 'guided-response' && activeDraft) {
    // Get sibling drafts for multi-question awareness
    const siblingDrafts = activeDraft.applicationGroupId
      ? essayDrafts.filter(d =>
          d.applicationGroupId === activeDraft.applicationGroupId && d.id !== activeDraft.id
        )
      : [];

    return (
      <GuidedResponse
        draft={activeDraft}
        siblingDrafts={siblingDrafts}
        onSave={handleGuidedSave}
        onBack={handleBack}
      />
    );
  }

  if (view === 'builder' && activeDraft) {
    return (
      <div>
        {/* Prompt context bar */}
        <div className="bg-violet-50 border-b border-violet-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-xs text-violet-600 font-medium mb-0.5">Essay Prompt</p>
              <p className="text-sm text-slate-800 truncate">{activeDraft.promptText}</p>
            </div>
            <button
              onClick={handleBack}
              className="ml-3 text-xs text-violet-600 hover:text-violet-800 font-medium shrink-0"
            >
              Back to List
            </button>
          </div>
          {activeDraft.wordLimit && (
            <p className="text-xs text-violet-500 mt-1">Word limit: {activeDraft.wordLimit}</p>
          )}
        </div>

        {/* Builder */}
        <div style={{ height: 'calc(100vh - 340px)', minHeight: '500px', overflow: 'auto' }}>
          <ConversationalEssayBuilder
            selectedEssay={getSelectedEssay()}
            onSaveEssay={handleSaveEssay}
            onBack={handleBack}
            initialTemplate={activeDraft.templateId}
            templateRegistry={SUMMER_PROGRAM_TEMPLATE_REGISTRY}
            onWeave={handleWeave}
            renderTemplateSelection={renderTemplateSelection}
            wordGoal={getWordGoal()}
          />
        </div>
      </div>
    );
  }

  if (view === 'refinement' && activeDraft) {
    return (
      <div>
        {/* Context bar */}
        <div className="bg-violet-50 border-b border-violet-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-xs text-violet-600 font-medium mb-0.5">Refining Essay</p>
              <p className="text-sm text-slate-800 truncate">{activeDraft.promptText}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0 ml-3">
              <button
                onClick={() => setView('builder')}
                className="text-xs text-violet-600 hover:text-violet-800 font-medium"
              >
                Back to Builder
              </button>
              <button
                onClick={handleBack}
                className="text-xs text-stone-500 hover:text-stone-700 font-medium"
              >
                Back to List
              </button>
            </div>
          </div>
        </div>

        {/* Refinement Interface */}
        <div style={{ minHeight: '600px' }}>
          <EssayRefinementInterface
            initialEssayContent={activeDraft.content}
            essayTitle={activeDraft.promptText?.slice(0, 60) || 'Essay Draft'}
            essayId={activeDraft.id}
            onNavigateBack={handleBack}
            onSwitchToBuilder={() => setView('builder')}
            onLoadAnalysis={handleLoadAnalysis}
            onSaveAnalysis={handleSaveAnalysis}
            onSaveDraft={handleSaveDraft}
          />
        </div>
      </div>
    );
  }

  // Default: list view
  return (
    <EssayListView
      onSelectEssay={handleSelectEssay}
      onAddNew={() => setView('new')}
    />
  );
};

export default EssayBuilder;
